import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { EXPLAINER_CARD, FONT_SIZE, INK, LINE_HEIGHT, NEUTRAL } from "@/theme/tokens";
import { AccentIconTile, type AccentIconTileTone } from "../molecules/AccentIconTile";
import { EyebrowLabel } from "../molecules/EyebrowLabel";

/** `card` holds the card's reading measure (inside a `HoverCard`); `fill` takes its container's width (a sheet, a panel). */
export type ExplainerCardWidth = "card" | "fill";

type Props = {
	/** The glyph in the header tile — the kind of thing being explained. */
	icon: ReactNode;
	/** Colours the header tile: which bucket the thing belongs to. */
	tone: AccentIconTileTone;
	/** Outlines a cause that was not recorded. */
	dashed?: boolean;
	/** Already translated. The question the card answers ("Why this shift was posted"). */
	eyebrow: string;
	/** The answer in a few words ("Call-off", "3 causes"). */
	title: ReactNode;
	/** The explanation — a sentence, a list of causes, a meter. Bold runs (`<b>`) are set in the primary ink. */
	children: ReactNode;
	/** The facts it rests on, under a hairline (a `FactList`). Absent = none. */
	facts?: ReactNode;
	/**
	 * A muted closing band (a total, a caveat). Pinned to the bottom of whatever scrolls the card (a `HoverCard`, a
	 * sheet), so a long explanation never pushes its total out of view. Absent = none.
	 */
	footer?: ReactNode;
	width: ExplainerCardWidth;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const INSET_X = 1.75;

/**
 * "Why is this here": a toned header (tile + eyebrow + a few-word answer), the explanation, then the facts it rests on
 * and an optional footer. Carries no surface of its own, so the same card reads in a `HoverCard`, a bottom sheet, or a
 * bordered panel the caller draws.
 */
export const ExplainerCard: FC<Props> = ({ icon, tone, dashed, eyebrow, title, children, facts, footer, width, dataTestId, sx }) => (
	<Box
		data-testid={dataTestId}
		sx={[
			{ width: width === "card" ? EXPLAINER_CARD.WIDTH : "100%", maxWidth: "100%", color: INK.PRIMARY },
			...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
		]}
	>
		<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: INSET_X, pt: 1.5, pb: 1.25 }}>
			<AccentIconTile size="xs" tone={tone} dashed={dashed}>
				{icon}
			</AccentIconTile>
			<Box sx={{ minWidth: 0 }}>
				<EyebrowLabel sx={{ lineHeight: LINE_HEIGHT.TIGHT }}>{eyebrow}</EyebrowLabel>
				<Typography component="div" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, lineHeight: LINE_HEIGHT.TIGHT, color: INK.PRIMARY }}>
					{title}
				</Typography>
			</Box>
		</Box>
		<Box
			sx={{
				px: INSET_X,
				pb: 1.5,
				fontSize: FONT_SIZE.SECONDARY,
				lineHeight: LINE_HEIGHT.RELAXED,
				color: INK.SECONDARY,
				"& b, & strong": { color: INK.PRIMARY, fontWeight: 600 },
			}}
		>
			{children}
		</Box>
		{facts !== undefined && <Box sx={{ borderTop: `1px solid ${NEUTRAL[150]}`, px: INSET_X, pt: 1.25, pb: 1.5 }}>{facts}</Box>}
		{footer !== undefined && (
			<Box
				sx={{
					position: "sticky",
					bottom: 0,
					zIndex: 1,
					borderTop: `1px solid ${NEUTRAL[150]}`,
					bgcolor: NEUTRAL[50],
					px: INSET_X,
					py: 1,
					fontSize: FONT_SIZE.LABEL_SM,
					color: INK.MUTED,
				}}
			>
				{footer}
			</Box>
		)}
	</Box>
);
